import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserStore } from '@citiguide/frontend-core';
import { Button, IconButton, useToast } from '@citiguide/ui-components';
import type { ChatNeed, ChatResponse, ChatStatusResponse } from '@citiguide/shared';
import { useChatTurn } from '../../hooks/useChatTurn';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useCurationStore } from '../../store/curationStore';
import { useLanguageStore } from '../../store/languageStore';
import { useMarkerStore } from '../../store/markerStore';
import { useMenuStore } from '../../store/menuStore';
import { usePlansStore } from '../../store/plansStore';
import PinnedPlaces from '../plans/PinnedPlaces';
import { applyChatResponse } from './applyChatResponse';
import type { AppliedResponse } from './applyChatResponse';
import ChatTranscript from './ChatTranscript';
import StateChips from './StateChips';
import './chat.css';

const MAX_INPUT = 1000;

const QUICK_REPLIES: Record<Exclude<ChatNeed, 'location'>, string[]> = {
  party: ['solo', 'couple', 'family', 'seniors'],
  interests: ['food', 'culture', 'nature'],
  budget: ['free', 'cheap', 'any'],
};

const formatDistance = (m: number): string => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);

interface ChatPanelProps {
  status: ChatStatusResponse;
}

export default function ChatPanel({ status }: ChatPanelProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const isDesktop = useIsDesktop();
  const isExpanded = useMenuStore((s) => s.isChatExpanded);
  const setChatOpen = useMenuStore((s) => s.setChatOpen);
  const setChatExpanded = useMenuStore((s) => s.setChatExpanded);
  const messages = useCurationStore((s) => s.messages);
  const clearTranscript = useCurationStore((s) => s.clearTranscript);
  const pinnedIds = useCurationStore((s) => s.pinnedMarkerIds);
  const togglePin = useCurationStore((s) => s.togglePin);
  const openPlan = useCurationStore((s) => s.openPlan);
  const closePlan = useCurationStore((s) => s.closePlan);
  const setSelectedMarker = useMarkerStore((s) => s.setSelectedMarker);
  const showRoute = useMarkerStore((s) => s.showRoute);
  const openPlansModal = usePlansStore((s) => s.openModal);
  const hasLocation = useUserStore((s) => s.location !== null);
  const setLocation = useUserStore((s) => s.setLocation);

  const [input, setInput] = useState('');
  const [applied, setApplied] = useState<AppliedResponse | null>(null);
  const [pendingText, setPendingText] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const wasExpanded = useRef(isExpanded);

  const onResponse = useCallback(
    (response: ChatResponse) => {
      const store = useCurationStore.getState();
      const language = useLanguageStore.getState().currentLanguage;
      const result = applyChatResponse(response, {
        applyState: store.applyServerState,
        appendAssistant: (text) => store.appendMessage({ role: 'assistant', content: text }),
        selectMarker: (id) => useMarkerStore.getState().setSelectedMarker(id),
        showRoute: (type) => useMarkerStore.getState().showRoute(type),
        hasLocation: useUserStore.getState().location !== null,
        pinnedMarkerIds: store.pinnedMarkerIds,
        savePlan: (plan) => usePlansStore.getState().save(plan),
        defaultPlanName: t('chat.defaultPlanName', { date: new Date().toLocaleDateString(language) }),
      });
      setApplied(result);
      if (result.savedPlanName) showToast(t('chat.saved', { name: result.savedPlanName }), 'success');
    },
    [t, showToast],
  );

  const turn = useChatTurn({ statusMode: status.mode, onResponse });
  const resting = status.mode === 'chips_only' || turn.unavailable || !!applied?.resting;
  const inputDisabled = status.mode === 'chips_only' || turn.unavailable || turn.cooling || turn.isPending;
  const showBody = isDesktop || isExpanded;

  // Opening moves focus to the input (when the body is visible).
  useEffect(() => {
    if (showBody) inputRef.current?.focus();
    // only when the panel first mounts / the body first appears
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBody]);

  // Re-focus the input when a turn finishes (keyboard-only flow).
  useEffect(() => {
    if (!turn.isPending && showBody && !inputDisabled) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn.isPending]);

  // Collapsing unmounts the input; keep keyboard focus inside the panel on the expand toggle.
  useEffect(() => {
    if (wasExpanded.current && !isExpanded) toggleRef.current?.focus();
    wasExpanded.current = isExpanded;
  }, [isExpanded]);

  const submit = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || inputDisabled) return;
    setPendingText(trimmed);
    setApplied(null);
    const ok = await turn.send(trimmed);
    setPendingText(null);
    if (ok) setInput('');
    else setInput((cur) => cur || trimmed);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape') return;
    if (!isDesktop && isExpanded) setChatExpanded(false);
    else setChatOpen(false);
  };

  const shareLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation([pos.coords.latitude, pos.coords.longitude]),
      () => undefined,
    );
  };

  const errorText =
    turn.error === 'rate_limited'
      ? t('chat.rateLimited', { seconds: turn.cooldownSeconds })
      : turn.error === 'busy'
        ? t('chat.busy')
        : turn.error === 'failed'
          ? t('chat.failed')
          : null;

  const unsupportedText = applied?.unsupported.length
    ? t('chat.unsupported.hint', { items: applied.unsupported.map((u) => t(`chat.unsupported.${u}`)).join(', ') })
    : null;

  return (
    <aside
      role="complementary"
      aria-label={t('chat.title')}
      className={`chat-panel ${isDesktop ? 'chat-panel--side' : 'chat-panel--sheet'}${showBody ? ' chat-panel--expanded' : ''}`}
      onKeyDown={onKeyDown}
    >
      <header className="chat-panel__header">
        <h2 className="chat-panel__title">{t('chat.title')}</h2>
        <div className="chat-panel__header-actions">
          {!isDesktop && (
            <button
              ref={toggleRef}
              type="button"
              className="chat-panel__icon-btn"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? t('chat.collapse') : t('chat.expand')}
              onClick={() => setChatExpanded(!isExpanded)}
            >
              {isExpanded ? '▾' : '▴'}
            </button>
          )}
          <IconButton
            icon={<span aria-hidden="true">×</span>}
            label={t('chat.close')}
            variant="ghost"
            size="sm"
            onClick={() => setChatOpen(false)}
          />
        </div>
      </header>

      <StateChips fields={status.fields} />

      {showBody && (
        <div className="chat-panel__body">
          {openPlan && openPlan.id && (
            <p className="chat-open-plan">
              {t('plans.shared', { name: openPlan.name })}
              <button type="button" className="chat-panel__icon-btn" aria-label={t('plans.closeBanner')} onClick={closePlan}>
                ×
              </button>
            </p>
          )}
          <ChatTranscript messages={messages} pendingText={pendingText} thinking={turn.thinking} longWait={turn.longWait} />

          {resting && <p className="chat-notice" role="status">{t('chat.resting')}</p>}
          {errorText && <p className="chat-notice chat-notice--error" role="alert">{errorText}</p>}
          {unsupportedText && <p className="chat-notice">{unsupportedText}</p>}
          {applied?.corrections.map((c, i) => (
            <p key={i} className="chat-notice">
              {t('chat.corrections.adjusted', { field: t(`chat.corrections.${c.field}`) })}
            </p>
          ))}
          {applied?.directionsNeedLocation && <p className="chat-notice">{t('chat.directionsNeedLocation')}</p>}
          {applied?.savedPlanName && (
            <p className="chat-notice">
              {t('chat.saved', { name: applied.savedPlanName })}{' '}
              <Button size="sm" variant="soft" onClick={openPlansModal}>{t('chat.openMyPlans')}</Button>
            </p>
          )}
          {applied && applied.didYouMean.length > 0 && (
            <div className="chat-didyoumean">
              <span>{t('chat.didYouMean')}</span>
              {applied.didYouMean.map((c) => (
                <Button
                  key={c.id}
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setSelectedMarker(c.id);
                    showRoute('walking');
                  }}
                >
                  {c.title}
                </Button>
              ))}
            </div>
          )}

          {applied && applied.items.length > 0 && (
            <section className="chat-results" aria-label={t('chat.results.title', { count: applied.total })}>
              <h3 className="chat-results__title">{t('chat.results.title', { count: applied.total })}</h3>
              <ul className="chat-results__list">
                {applied.items.map((card) => {
                  const pinned = pinnedIds.includes(card.id);
                  return (
                    <li key={card.id} className="chat-card">
                      <button type="button" className="chat-card__open" onClick={() => setSelectedMarker(card.id)}>
                        <span className="chat-card__title">{card.title}</span>
                        <span className="chat-card__meta">
                          {t(`chat.results.kind.${card.kind}`)}
                          {card.distance_m != null && ` · ${t('chat.results.away', { distance: formatDistance(card.distance_m) })}`}
                        </span>
                      </button>
                      <Button
                        size="sm"
                        variant={pinned ? 'soft' : 'secondary'}
                        aria-pressed={pinned}
                        onClick={() => togglePin(card.id)}
                      >
                        {pinned ? t('chat.results.unpin') : t('chat.results.pin')}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <PinnedPlaces />

          {applied && applied.needs.length > 0 && !inputDisabled && (
            <div className="chat-quick" role="group">
              {applied.needs.map((need) =>
                need === 'location' ? (
                  !hasLocation && (
                    <Button key="location" size="sm" variant="soft" onClick={shareLocation}>
                      {t('chat.needs.shareLocation')}
                    </Button>
                  )
                ) : (
                  QUICK_REPLIES[need].map((option) => {
                    const text = t(`chat.needs.${need}.${option}`);
                    return (
                      <Button key={`${need}-${option}`} size="sm" variant="soft" onClick={() => void submit(text)}>
                        {text}
                      </Button>
                    );
                  })
                ),
              )}
            </div>
          )}

          <form
            className="chat-input"
            onSubmit={(e) => {
              e.preventDefault();
              void submit(input);
            }}
          >
            <textarea
              ref={inputRef}
              className="chat-input__field"
              rows={2}
              maxLength={MAX_INPUT}
              aria-label={t('chat.inputLabel')}
              placeholder={t('chat.inputPlaceholder')}
              value={input}
              disabled={inputDisabled}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submit(input);
                }
              }}
            />
            <Button type="submit" size="md" disabled={inputDisabled || !input.trim()}>
              {t('chat.send')}
            </Button>
          </form>
          {messages.length > 0 && (
            <button type="button" className="chat-clear" onClick={() => { clearTranscript(); setApplied(null); }}>
              {t('chat.clear')}
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
